import { AddCollectionProductToCartRequest } from '@/communication/AddCollectionProductToCartRequest';
import { AddToCartRequest } from '@/communication/AddToCartRequest';
import { UpdateCartItemRequest } from '@/communication/UpdateCartItemRequest';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { useFilterStore } from '@/stores/Configurator/FilterStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { ConfigurableProductSizeValueObject } from '@/types/joostconnector';
import { reactive, Ref, ref } from 'vue';
import { useContinueToken } from './UseContinueToken';
import { useGoogleTagManager } from './UseGoogleTagManager';
import { useMediaclipCommunication } from './UseMediaclipCommunication';
import { useBroadcast } from '@/composables/UseBroadcast';
import { useQuoteStore } from '@/stores/Quote/QuoteStore';
import { useSizeStore } from '@/stores/Configurator/SizeStore.ts';

export function useAddToCart() {
  const extraOptionsStore = useExtraOptionsStore();
  const filterStore = useFilterStore();
  const sizeStore = useSizeStore();
  const quoteStore = useQuoteStore();
  const configuratorStore = useConfiguratorStore();
  const googleTagManager = useGoogleTagManager();
  const broadcast = useBroadcast();
  const addConfiguratorProductToCartRequest = reactive(new AddToCartRequest());
  const addCollectionProductToCartRequest = reactive(
    new AddCollectionProductToCartRequest()
  );
  const updateRequest = reactive(new UpdateCartItemRequest());

  const isRequesting: Ref<boolean> = ref(false);
  const isAwaitingRedirect: Ref<boolean> = ref(false);

  async function addConfiguratorProductToCart(
    selectedProductSku: string,
    mediaclipProductId: string
  ): Promise<void> {
    // Validation
    configuratorValidation();

    isAwaitingRedirect.value = true;
    isRequesting.value = false;

    try {
      configuratorStore.isUpdatingProject = true;

      // Save the project and state in a database to make sure all data is present on error or refresh
      await useMediaclipCommunication().saveProject();
      await useContinueToken().saveStateInDatabase();

      await (
        configuratorStore.quoteItemId
          ? updateRequest
          : addConfiguratorProductToCartRequest
      )
        .setSelectedProductSku(selectedProductSku)
        .setMagentoProductId(configuratorStore.product.entity_id)
        .setMediaclipProjectId(configuratorStore.mediaclipProjectId)
        .setMediaclipProductId(mediaclipProductId)
        .setMediaclipPreviewUrl(configuratorStore.mediaclipPreviewUrl)
        .setExtraOptions(
          extraOptionsStore.selectedOptions,
          extraOptionsStore.productOptionsCategories
        )
        .setFilters(filterStore.filtersForCart)
        .getResponse();
    } catch (e) {
      configuratorStore.isUpdatingProject = false;
      isAwaitingRedirect.value = false;
      isRequesting.value = false;
      throw e;
    }

    configuratorStore.resetData(false);
    googleTagManager.triggerConfiguratorAddToCartEvent();

    emitAddedQuoteItemEvent();

    window.location.href = window.cart_url;
  }

  function configuratorValidation(): void {
    if (!localStorage.getItem(configuratorStore.$id)) {
      window.location.reload();
      throw new Error(
        'No session found. User potentially already added mediaclip product to cart'
      );
    }
    if (!configuratorStore.mediaclipProjectId) {
      throw new Error(
        'Mediaclip project id not set when adding product to cart'
      );
    }
  }

  async function addCollectionProductToCart(
    selectedSizeValue: ConfigurableProductSizeValueObject
  ): Promise<void> {
    try {
      isRequesting.value = true;

      await addCollectionProductToCartRequest
        .setProductSku(selectedSizeValue.attributes.sku)
        .setExtraOptions(
          extraOptionsStore.selectedOptions,
          extraOptionsStore.productOptionsCategories
        )
        .setProductId(window.product.entity_id)
        .setConfigurableCollectionId(window.configurable_collection_id)
        .setPrintImageId(window.print_image_id)
        .setImageUrl(selectedSizeValue.images[0]?.full)
        .setEan(selectedSizeValue.attributes.ean)
        .getResponse();
    } catch (e) {
      isRequesting.value = false;
      throw e;
    }

    isAwaitingRedirect.value = true;
    isRequesting.value = false;

    localStorage.removeItem(extraOptionsStore.$id);
    localStorage.removeItem(`product-${window.product.entity_id}-price`);
    googleTagManager.triggerCollectionAddToCartEvent(
      sizeStore.selectedSizeValue
    );

    emitAddedQuoteItemEvent();

    window.location.href = window.cart_url;
  }

  function emitAddedQuoteItemEvent(): void {
    const quoteId = quoteStore.quoteId;
    if (quoteId) {
      broadcast.emitQuoteEvent('added-quote-item', quoteId);
    }
  }

  return {
    addConfiguratorProductToCartRequest,
    addCollectionProductToCartRequest,
    updateRequest,
    isRequesting,
    isAwaitingRedirect,
    addConfiguratorProductToCart,
    addCollectionProductToCart,
  };
}
